import React, { FC, useEffect, useState } from "react";

import "./Feedback.scss";

import useAppStore from "../Store/AppStore";
import { useTranslation } from "react-i18next";
import CloseBtn from "./components/CloseBtn";
import { useMsal } from "@azure/msal-react";
import { Constants } from "./utils/constants";
import { EmailClient, KnownEmailSendStatus } from "@azure/communication-email";
import { processFiles } from "./utils/fileHandling";
import { setKeyVaultSecret } from "../../service/apiService";
import { runtimeConfig } from "../../runtimeConfig";

const Feedback: FC = () => {
    const { t } = useTranslation();
    const { accounts } = useMsal();
    const [email, setEmail] = useState<string>("");
    const [title, setTitle] = useState<string>("");
    const [isTitleMissing, setIsTitleMissing] = useState<boolean>(false);
    const [isDescriptionMissing, setIsDescriptionMissing] =
        useState<boolean>(false);
    const [description, setDescriptionChange] = useState<string>("");
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [showInput, setShowInput] = useState(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
        const userEmail = accounts[0].idTokenClaims.email as string;
        setEmail(userEmail);
    }, [accounts]);

    const setCurrentPopup = useAppStore((state) => state.setCurrentPopup);

    const sendEmail = async () => {
        setIsLoading(true);
        let filesData;

        try {
            filesData = await processFiles(uploadedFiles);
        } catch (error) {
            console.error("Error reading files:", error);
        }

        const message = {
            senderAddress: runtimeConfig.DOMAIN_EMAIL,
            content: {
                subject: `${Constants.feedbackPrefix} ${title}`,
                html: Constants.feedbackTemplate(
                    email,
                    description,
                    uploadedFiles?.length
                ),
            },
            recipients: {
                to: [
                    {
                        address: email,
                        displayName: email,
                    },
                ],
                cc: [
                    {
                        address: Constants.feedbackEmail,
                        displayName: email,
                    },
                ],
            },
            attachments: filesData ? filesData : [],
        };
        
        const client = new EmailClient(
            await setKeyVaultSecret("COMSERVICE-CONNECTIONSTRING",false)
        );

        try {
            const poller = await client.beginSend(message);
            await poller.pollUntilDone();
            const pollerResult = poller.getResult();
            if (
                pollerResult &&
                pollerResult.status === KnownEmailSendStatus.Succeeded
            ) {
                setCurrentPopup("thankyou");
            } else {
                console.error("Error sending email:", pollerResult.error);
            }
        } catch (error) {
            console.error("Error during polling operation:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmitFeedback = () => {
        if (!title) {
            setIsTitleMissing(true);
        }

        if (!description) {
            setIsDescriptionMissing(true);
        }

        if (!title || !description) {
            return;
        }

        sendEmail();
    };

    const handleDescriptionChange = (e: any) => {
        setDescriptionChange(e.target.value);
        setIsDescriptionMissing(false);
    };

    const handleChangeTitle = (e: any) => {
        setTitle(e.target.value);
        setIsTitleMissing(false);
    };

    const handleAttachFiles = (e: any) => {
        const chosenFiles = Array.from(e.target.files);
        setUploadedFiles((prev) => prev.concat(chosenFiles));
    };

    const handleFeedbackFocus = (e: any) => {
        setShowInput(true);
    };

    return (
        <div className="flex flex-col w-[315px] max-h-[577px] p-5 bg-white rounded-md">
            <CloseBtn />
            <h2 className="popup-title">{t("Feedback")}</h2>
            <div className="email-wrapper mt-[15px]">
                <p className="section-title">{t("Email")}</p>
                <input
                    type="email"
                    className="feedback-input input-border w-full h-[35px] pl-[5px] input-text disabled-background"
                    placeholder={email}
                    disabled
                />
            </div>
            <div className="subject-wrapper">
                <p className="section-title">{t("Subject")}</p>
                <div className="input-box">
                    <input
                        className={`feedback-input w-full input-border h-[35px] input-text ${showInput ? "disabled-background" : ""
                            }`}
                        type="text"
                        value={Constants.feedbackPrefix}
                        onFocus={handleFeedbackFocus}
                        disabled={showInput}
                    />
                    <input
                        type="text"
                        className={`feedback-input w-full input-border h-[35px] input-text ${showInput ? "inline-block" : "hidden"
                            }`}
                        onChange={handleChangeTitle}
                        value={title}
                        required
                    />
                </div>
                <div
                    className={`${isTitleMissing ? "error-shown" : "error-hidden"
                        } section-title text-sersa mt-1`}
                >
                    {t("Please enter a subject")}
                </div>
            </div>
            <div className="feedback-wrapper">
                <p className="section-title">{t("Feedback")}</p>
                <textarea
                    className="input-border w-full h-[113px] input-text textarea"
                    onChange={handleDescriptionChange}
                    value={description}
                    required
                />
                <div
                    className={`${isDescriptionMissing ? "error-shown" : "error-hidden"
                        } section-title text-sersa mt-0`}
                >
                    {t("Please enter a description")}
                </div>
            </div>
            <div className="attachments-wrapper">
                <p className="section-title">{t("Attachments")}</p>
                <input
                    id="upload"
                    type="file"
                    hidden
                    multiple
                    accept=".png, .jpeg, .jpg, .img, .tif, .tiff, .bmp"
                    onChange={handleAttachFiles}
                />
                {uploadedFiles?.length > 0 && (
                    <div className="section-title">
                        {uploadedFiles?.length} {t("files attached")}
                    </div>
                )}
                <label
                    className="button-primary button-border-gray button-text flex justify-center items-center cursor-pointer"
                    htmlFor="upload"
                >
                    {t("Add data")}
                </label>
            </div>
            <div className="submit-feedback-wrapper">
                {isLoading ? (
                    <div className="throbber">
                        <div className="loader"></div>
                        <span className="sending-text">{t("Sending")}</span>
                    </div>
                ) : (
                    <button
                        className="button-primary bg-sersa text-white button-text flex justify-center items-center"
                        onClick={handleSubmitFeedback}
                    >
                        {t("Submit feedback")}
                    </button>
                )}
            </div>
        </div>
    );
};

export default Feedback;
